import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const { Title, Text } = Typography;

const PayrollSecurityAudit = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const query = useCallback(
    (exportFormat) => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
      if (exportFormat) params.set("export", exportFormat);
      return `${domainSettings.urls.finance.payrollSecurityAudit}?${params}`;
    },
    [filters],
  );
  const load = useCallback(async () => {
    setLoading(true);
    const response = await Utilities.fetchWithAuth(
      "GET",
      query(),
      null,
      navigate,
    );
    if (response.ok) setData(Array.isArray(response.data) ? response.data : []);
    else Utilities.showError(response, "Could not load payroll security audit");
    setLoading(false);
  }, [navigate, query]);
  useEffect(() => {
    load();
  }, [load]);
  const download = async (format) => {
    const response = await fetch(query(format), {
      headers: { Authorization: `Bearer ${Utilities.getCookie("access")}` },
    });
    if (!response.ok)
      return Utilities.showError(
        { status: response.status },
        "Could not export payroll audit",
      );
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = `payroll_security_audit.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Title level={3} style={{ marginBottom: 4 }}>
          Payroll security audit
        </Title>
        <Text type="secondary">
          A permanent, redacted record of payroll views, changes, exports,
          downloads, emails, approvals, and denied attempts.
        </Text>
      </div>
      <Card>
        <Space wrap>
          <Select
            allowClear
            placeholder="Event"
            style={{ width: 180 }}
            options={[
              "Sensitive View",
              "Payroll Change",
              "Workflow Action",
              "Sensitive Export",
              "Payslip Access",
              "Payslip Email",
              "Deletion Attempt",
            ].map((value) => ({ value, label: value }))}
            onChange={(eventType) =>
              setFilters((old) => ({ ...old, eventType }))
            }
          />
          <Select
            allowClear
            placeholder="Outcome"
            style={{ width: 140 }}
            options={["Succeeded", "Denied", "Failed"].map((value) => ({
              value,
              label: value,
            }))}
            onChange={(outcome) => setFilters((old) => ({ ...old, outcome }))}
          />
          <Input.Search
            allowClear
            placeholder="API path"
            style={{ width: 230 }}
            onSearch={(path) => setFilters((old) => ({ ...old, path }))}
          />
          <DatePicker.RangePicker
            onChange={(dates) =>
              setFilters((old) => ({
                ...old,
                start: dates?.[0]?.format("YYYY-MM-DD"),
                end: dates?.[1]?.format("YYYY-MM-DD"),
              }))
            }
          />
          <Button icon={<ReloadOutlined />} onClick={load}>
            Refresh
          </Button>
          <Button icon={<DownloadOutlined />} onClick={() => download("csv")}>
            CSV
          </Button>
          <Button icon={<DownloadOutlined />} onClick={() => download("xlsx")}>
            Excel
          </Button>
        </Space>
      </Card>
      <Card bodyStyle={{ padding: 0 }}>
        <Table
          loading={loading}
          rowKey="auditId"
          dataSource={data}
          scroll={{ x: 1200 }}
          columns={[
            {
              title: "Time",
              dataIndex: "createdAt",
              render: (value) => dayjs(value).format("DD MMM YYYY HH:mm:ss"),
            },
            { title: "Event", dataIndex: "eventType" },
            {
              title: "Outcome",
              dataIndex: "outcome",
              render: (value) => (
                <Tag
                  color={
                    value === "Succeeded"
                      ? "green"
                      : value === "Denied"
                        ? "red"
                        : "orange"
                  }
                >
                  {value}
                </Tag>
              ),
            },
            { title: "User", dataIndex: "actorName" },
            { title: "Role used", dataIndex: "roleName" },
            { title: "Action", dataIndex: "action" },
            {
              title: "Object",
              render: (_, row) =>
                [row.objectType, row.objectId].filter(Boolean).join(" ") || "—",
            },
            {
              title: "Sensitive areas",
              dataIndex: "sensitivity",
              render: (value) =>
                value?.map((item) => <Tag key={item}>{item}</Tag>),
            },
            { title: "Path", dataIndex: "path" },
            { title: "Status", dataIndex: "statusCode" },
          ]}
        />
      </Card>
    </Space>
  );
};
export default PayrollSecurityAudit;
